import type * as React from 'react'
import { useState } from 'react'

import { useMutation, type ApolloCache } from '@apollo/client'

import {
  AlertDialog,
  Button,
  Card,
  ChannelAvatar,
  type ChannelType,
  Flex,
  Form,
  Input,
  Link,
  Text,
  toast,
  Tooltip,
} from '@buffer-mono/popcorn'

import { GetSidebarInfo } from '~publish/components/PublishSidebar/useChannels'
import { logError } from '~publish/legacy/utils/logError'

import { RemoveChannelsMutation } from './ChannelDropdownItems'
import { type ChannelMask, GetChannels } from '../hooks/useChannelsForSettings'
import { isStartPage } from '../util/isStartPage'

import style from './DisconnectChannelAlertDialog.module.css'

const updateCacheAfterChannelRemoval = (
  cache: ApolloCache<unknown>,
  channelToRemove: ChannelMask,
): void => {
  // Update Settings page query - remove channel from GetChannels
  const settingsData = cache.readQuery({
    query: GetChannels,
    variables: { input: { organizationId: channelToRemove.organizationId } },
  })
  if (settingsData?.channels) {
    cache.writeQuery({
      query: GetChannels,
      variables: { input: { organizationId: channelToRemove.organizationId } },
      data: {
        ...settingsData,
        channels: settingsData.channels.filter(
          (c) => c?.id !== channelToRemove.id,
        ),
      },
    })
  }

  // Update Sidebar query - remove channel from GetSidebarInfo
  const sidebarData = cache.readQuery({ query: GetSidebarInfo })
  if (sidebarData?.account?.currentOrganization?.channels) {
    const updatedChannels =
      sidebarData.account.currentOrganization.channels.filter(
        (c) => c?.id !== channelToRemove.id,
      )
    cache.writeQuery({
      query: GetSidebarInfo,
      data: {
        ...sidebarData,
        account: {
          ...sidebarData.account,
          currentOrganization: {
            ...sidebarData.account.currentOrganization,
            channels: updatedChannels,
          },
        },
      },
    })
  }
}

const DisconnectChannelAlertDialog = ({
  channel,
  children,
  handleRefreshChannelConnection,
}: {
  channel: ChannelMask
  children: React.ReactNode
  handleRefreshChannelConnection: (cta: string) => void
}): JSX.Element => {
  const [inputValue, setInputValue] = useState('')
  const [hasInputError, setHasInputError] = useState(false)
  const [executeRemoveChannels, { loading }] = useMutation(
    RemoveChannelsMutation,
  )

  const isFacebookGroup =
    channel.service === 'facebook' && channel.type === 'group'
  const isInstagramPersonalProfile =
    channel.service === 'instagram' && channel.type === 'profile'
  const isChannelStartPage = isStartPage({ channel })
  const shouldShowRefreshConnection =
    !isFacebookGroup && !isInstagramPersonalProfile && !isChannelStartPage

  const successMessage = isChannelStartPage
    ? 'Channel deleted successfully!'
    : 'Channel disconnected successfully!'

  const errorMessage = isChannelStartPage
    ? 'Failed to delete channel'
    : 'Failed to disconnect channel'

  const disconnectConfirmationText = 'disconnect'
  const isDisconnectApproved =
    inputValue.trim().toLowerCase() === disconnectConfirmationText.toLowerCase()

  const shouldShowInputError = hasInputError && !isDisconnectApproved

  const handleConfirmDisconnect = async (): Promise<void> => {
    try {
      const { data, errors } = await executeRemoveChannels({
        variables: {
          channelIds: [channel.id],
        },
        update: (cache) => updateCacheAfterChannelRemoval(cache, channel),
      })

      if (data?.channelsRemove) {
        toast.success(successMessage)
      }

      if (errors?.length) {
        toast.error(errorMessage)
        const unexpectedError = new Error(
          errors?.[0]?.message || 'Unexpected API response',
        )
        logError(unexpectedError, { metaData: { data, errors } })
      }
    } catch (e) {
      toast.error(errorMessage)
      logError(e instanceof Error ? e : new Error(String(e)), {
        metaData: { variables: { channel } },
      })
    }
  }

  return (
    <AlertDialog>
      <Form>
        <AlertDialog.Trigger>{children}</AlertDialog.Trigger>
        <AlertDialog.Content>
          <AlertDialog.Title>
            {isChannelStartPage
              ? 'Delete Start Page'
              : `Disconnect ${channel.name}`}
          </AlertDialog.Title>
          <AlertDialog.Description className={style.body}>
            <Flex as="section">
              <Card padding="small" className={style.channelCard}>
                <ChannelAvatar
                  src={channel.avatar}
                  alt={channel.displayName || channel.name}
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  channel={channel.service as ChannelType}
                  size="small"
                />
                <Flex direction="column">
                  <Text weight="bold">{channel.name}</Text>
                  <Text color="subtle">
                    {isChannelStartPage ? 'Start Page' : channel.descriptor}
                  </Text>
                </Flex>
              </Card>
            </Flex>
            {isChannelStartPage ? (
              <Text>
                Your Start Page will be immediately deleted and your data will
                be lost. Visitors will no longer be able to access your page
                using the link.
              </Text>
            ) : (
              <>
                <Text>
                  You will no longer be able to post to this account. All posts,
                  analytics, tags, and data linked to it will be permanently
                  deleted. <strong>This cannot be undone.</strong>
                </Text>

                {shouldShowRefreshConnection && (
                  <Text>
                    Having issues with your connection? Try{' '}
                    <Link
                      as="button"
                      onClick={(): void => {
                        handleRefreshChannelConnection(
                          `disconnect-channel-refresh-connection-${channel.service.toLowerCase()}-1`,
                        )
                      }}
                    >
                      Refresh Connection
                    </Link>{' '}
                    first.
                  </Text>
                )}
              </>
            )}

            <Flex as="section" direction="column" gap="2xs">
              <Form.Field name="channelName" className={style.formField}>
                <Form.Label className={style.formLabel}>
                  Type &quot;{disconnectConfirmationText}&quot; to confirm.
                </Form.Label>
                <Form.Control>
                  <Input
                    className={style.fullWidth}
                    value={inputValue}
                    onChange={(e): void => setInputValue(e.target.value)}
                    placeholder={disconnectConfirmationText}
                    aria-invalid={shouldShowInputError}
                    aria-required
                    aria-describedby={
                      shouldShowInputError ? 'channelName-error' : undefined
                    }
                  />
                </Form.Control>
              </Form.Field>
              {shouldShowInputError && (
                <Text
                  id="channelName-error"
                  as="span"
                  color="critical"
                  size="sm"
                  aria-live="polite"
                  role="alert"
                >
                  Please type &quot;{disconnectConfirmationText}&quot; to
                  confirm this action.
                </Text>
              )}
            </Flex>
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel>
              <Button
                variant="tertiary"
                size="large"
                onClick={(): void => {
                  setInputValue('')
                  setHasInputError(false)
                }}
              >
                Cancel
              </Button>
            </AlertDialog.Cancel>
            {!isDisconnectApproved ? (
              <Tooltip
                content={`Please enter "${disconnectConfirmationText}" to confirm.`}
              >
                <Button
                  variant="critical"
                  size="large"
                  loading={loading}
                  disabled={inputValue.length === 0}
                  onClick={(): void => {
                    setHasInputError(true)
                  }}
                >
                  {isChannelStartPage
                    ? 'Confirm and Delete'
                    : 'Disconnect Channel'}
                </Button>
              </Tooltip>
            ) : (
              <Button
                variant="critical"
                size="large"
                loading={loading}
                disabled={loading}
                onClick={(): void => {
                  void handleConfirmDisconnect()
                }}
              >
                {isChannelStartPage
                  ? 'Confirm and Delete'
                  : 'Disconnect Channel'}
              </Button>
            )}
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </Form>
    </AlertDialog>
  )
}

export default DisconnectChannelAlertDialog
